import { useMemo, useState } from 'react';
import { downloadCsv } from '@/lib/csv';
import type { Commission } from '@/services/commissions';

function formatMoney(v: number): string {
  return `$${v.toLocaleString('es-CO')}`;
}

function formatDate(value: string): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function monthLabel(month: string): string {
  if (!month) return '';
  const [y, m] = month.split('-');
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString('es-CO', { month: 'long', year: 'numeric' });
}

interface CommissionReportProps {
  commissions: Commission[];
}

export default function CommissionReport({ commissions }: CommissionReportProps) {
  const now = new Date();
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [month, setMonth] = useState(currentMonth);

  const monthRows = useMemo(
    () => commissions.filter((c) => (c.createdAt || '').slice(0, 7) === month),
    [commissions, month],
  );

  const grouped = useMemo(() => {
    const map = new Map<string, { name: string; total: number; count: number; paid: number; pending: number }>();
    monthRows.forEach((c) => {
      const name = c.salespersonName || 'Sin vendedor';
      const g = map.get(name) || { name, total: 0, count: 0, paid: 0, pending: 0 };
      g.total += c.commissionAmount;
      g.count += 1;
      if (c.isPaid) g.paid += c.commissionAmount;
      else g.pending += c.commissionAmount;
      map.set(name, g);
    });
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [monthRows]);

  const totalCommission = monthRows.reduce((s, c) => s + c.commissionAmount, 0);
  const totalPaid = monthRows.filter((c) => c.isPaid).reduce((s, c) => s + c.commissionAmount, 0);
  const totalPending = totalCommission - totalPaid;

  function handleDownload() {
    downloadCsv(
      `comisiones-${month}`,
      ['Vendedor', 'Venta', 'Monto venta', 'Porcentaje', 'Comisión', 'Estado', 'Fecha'],
      monthRows.map((c) => [
        c.salespersonName || 'Sin vendedor',
        c.saleNumber || '—',
        c.saleAmount,
        `${c.commissionRate}%`,
        c.commissionAmount,
        c.isPaid ? 'Pagada' : 'Pendiente',
        formatDate(c.createdAt),
      ]),
    );
  }

  return (
    <section className="bg-white rounded-xl border border-background-200 overflow-hidden">
      <div className="px-5 py-4 border-b border-background-100 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-accent-50 flex items-center justify-center">
            <i className="ri-bar-chart-2-line text-accent-600"></i>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-foreground-900">Reporte Mensual</h2>
            <p className="text-xs text-foreground-500">Desglose de comisiones por mes y vendedor</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="text-sm border border-background-300 rounded-lg px-3 py-2 bg-white text-foreground-900 focus:outline-none focus:ring-2 focus:ring-accent-300"
          />
          <button
            onClick={handleDownload}
            disabled={monthRows.length === 0}
            className="text-sm px-4 py-2 bg-accent-500 text-white rounded-lg hover:bg-accent-600 transition-colors disabled:opacity-50 whitespace-nowrap font-medium cursor-pointer"
          >
            <i className="ri-download-2-line mr-1.5"></i>Descargar CSV
          </button>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {/* Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <div className="bg-background-50 rounded-lg p-4 border border-background-100">
            <p className="text-xs text-foreground-500">Total del mes</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{formatMoney(totalCommission)}</p>
          </div>
          <div className="bg-background-50 rounded-lg p-4 border border-background-100">
            <p className="text-xs text-foreground-500">Pagadas</p>
            <p className="text-xl font-bold text-emerald-600 mt-1">{formatMoney(totalPaid)}</p>
          </div>
          <div className="bg-background-50 rounded-lg p-4 border border-background-100">
            <p className="text-xs text-foreground-500">Pendientes</p>
            <p className="text-xl font-bold text-amber-600 mt-1">{formatMoney(totalPending)}</p>
          </div>
          <div className="bg-background-50 rounded-lg p-4 border border-background-100">
            <p className="text-xs text-foreground-500">Comisiones</p>
            <p className="text-xl font-bold text-foreground-950 mt-1">{monthRows.length}</p>
          </div>
        </div>

        {/* Grouped by seller */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-2">
            Desglose por vendedor · {monthLabel(month)}
          </h3>
          {grouped.length === 0 ? (
            <div className="text-center py-8 text-foreground-400 text-sm">
              No hay comisiones registradas en este mes.
            </div>
          ) : (
            <div className="border border-background-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-100 text-left bg-background-50">
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Vendedor</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500 text-right">Comisiones</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500 text-right">Total comisión</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500 text-right">Pendiente</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500 text-right">Pagada</th>
                  </tr>
                </thead>
                <tbody>
                  {grouped.map((g) => (
                    <tr key={g.name} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-4 py-2.5 text-foreground-900 font-medium">{g.name}</td>
                      <td className="px-4 py-2.5 text-right text-foreground-600">{g.count}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-foreground-950">{formatMoney(g.total)}</td>
                      <td className="px-4 py-2.5 text-right text-amber-600">{g.pending > 0 ? formatMoney(g.pending) : '—'}</td>
                      <td className="px-4 py-2.5 text-right text-emerald-600">{g.paid > 0 ? formatMoney(g.paid) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* History list */}
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-foreground-400 mb-2">
            Historial del mes ({monthRows.length})
          </h3>
          {monthRows.length === 0 ? (
            <div className="text-center py-8 text-foreground-400 text-sm">
              Selecciona un mes con comisiones para ver el detalle.
            </div>
          ) : (
            <div className="border border-background-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-100 text-left bg-background-50">
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Vendedor</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Venta</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500 text-right">Comisión</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500 text-center">Estado</th>
                    <th className="px-4 py-2.5 text-xs font-medium text-foreground-500">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {monthRows.map((c) => (
                    <tr key={c.id} className="border-b border-background-100 hover:bg-background-50 transition-colors">
                      <td className="px-4 py-2.5 text-foreground-900">{c.salespersonName || '—'}</td>
                      <td className="px-4 py-2.5 text-foreground-600 whitespace-nowrap">{c.saleNumber || '—'}</td>
                      <td className="px-4 py-2.5 text-right font-semibold text-foreground-950">{formatMoney(c.commissionAmount)}</td>
                      <td className="px-4 py-2.5 text-center">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${c.isPaid ? 'text-emerald-700 bg-emerald-50' : 'text-amber-700 bg-amber-50'}`}>
                          {c.isPaid ? 'Pagada' : 'Pendiente'}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-foreground-500 whitespace-nowrap">{formatDate(c.createdAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}